import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import { MessageCircle, Mail, Phone, HelpCircle, Send } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import * as supportService from "../services/supportService";

const SupportPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast({
        title: t("support.errorTitle"),
        description:
          "Vous devez être connecté pour envoyer un message de support.",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    if (!subject.trim() || !message.trim()) {
      toast({
        title: t("support.errorTitle"),
        description: t("support.emptyFields"),
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const result = await supportService.createSupportMessage({
        subject: subject.trim(),
        message: message.trim(),
      });

      toast({
        title: t("support.successTitle"),
        description: t("support.successDescription"),
        variant: "success",
      });
      setSubject("");
      setMessage("");

      // Rediriger vers les messages pour voir la conversation
      if (result.roomId) {
        setTimeout(() => {
          navigate("/messages");
        }, 1500);
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
          ? error.message
          : t("support.errorDescription");
      toast({
        title: t("support.errorTitle"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
            {t("support.title")}
          </h1>
          <p className="text-slate-600 dark:text-white/70">{t("support.subtitle")}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Options de contact */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-100/90 backdrop-blur-sm p-6 dark:border-white/10 dark:from-slate-900/50 dark:to-slate-800/30">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                {t("support.contactOptions")}
              </h2>
              <div className="space-y-4">
                <Link
                  to="/messages"
                  className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50/80 hover:bg-slate-100 transition dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10">
                  <MessageCircle className="h-5 w-5 text-cyan-400" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {t("support.chatSupport")}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-white/60">
                      {t("support.chatDescription")}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50/80 dark:border-white/10 dark:bg-white/5">
                  <Mail className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {t("support.emailSupport")}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-white/60">support@synapse.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50/80 dark:border-white/10 dark:bg-white/5">
                  <Phone className="h-5 w-5 text-emerald-400" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {t("support.phoneSupport")}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-white/60">+44 7451 298562</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-100/90 backdrop-blur-sm p-6 dark:border-white/10 dark:from-slate-900/50 dark:to-slate-800/30">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                {t("support.faq")}
              </h2>
              <div className="space-y-3">
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 dark:border-white/5 dark:bg-white/5">
                  <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">
                    {t("support.faq1.question")}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-white/60">
                    {t("support.faq1.answer")}
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 dark:border-white/5 dark:bg-white/5">
                  <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">
                    {t("support.faq2.question")}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-white/60">
                    {t("support.faq2.answer")}
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 dark:border-white/5 dark:bg-white/5">
                  <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">
                    {t("support.faq3.question")}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-white/60">
                    {t("support.faq3.answer")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-100/90 backdrop-blur-sm p-6 dark:border-white/10 dark:from-slate-900/50 dark:to-slate-800/30">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
              {t("support.contactForm")}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("support.subjectLabel")}</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("support.subjectPlaceholder")}
                  className="border-slate-200 bg-white text-slate-900 dark:bg-white/5 dark:text-white dark:border-white/10"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t("support.messageLabel")}</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("support.messagePlaceholder")}
                  className="border-slate-200 bg-white text-slate-900 min-h-[200px] dark:bg-white/5 dark:text-white dark:border-white/10"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={sending}
                variant="default">
                {sending ? (
                  <>
                    <HelpCircle className="mr-2 h-4 w-4 animate-spin" />
                    {t("support.sending")}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {t("support.sendButton")}
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
